import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import type { AdminOrganization } from '@/client/types/admin/users-orgs';
import { withErrorHandling } from '@/client/utils/errors';
import { keepPreviousData, useQuery } from '@tanstack/react-query';


const buildQs = (params?: Record<string, unknown>): string => {
  if (!params) return '';
  const clean = Object.entries(params).reduce<Record<string, string>>(
    (acc, [k, v]) => {
      if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
      return acc;
    },
    {}
  );
  const qs = new URLSearchParams(clean).toString();
  return qs ? `?${qs}` : '';
};

/** Accept either the `{ status, data }` envelope or a bare top-level payload. */
const unwrap = <T,>(raw: any): T =>
  raw != null && typeof raw === 'object' && 'status' in raw && 'data' in raw
    ? (raw.data as T)
    : (raw as T);

export const userContentKeys = {
  all: ['admin', 'user-content'] as const,
  entities: (uid: number | null, params: object) =>
    [...userContentKeys.all, 'entities', uid, params] as const,
  feed: (uid: number | null, params: object) =>
    [...userContentKeys.all, 'feed', uid, params] as const,
  organizations: (uid: number | null) =>
    [...userContentKeys.all, 'organizations', uid] as const,
};

const assertOkEnvelope = (raw: any): void => {
  if (
    raw &&
    typeof raw === 'object' &&
    typeof raw.status === 'number' &&
    raw.status >= 400
  ) {
    throw new Error(raw?.data?.error || `Request failed (${raw.status})`);
  }
};

/* ─────────────────────────── User's listings ───────────────────────────── */

export interface UserEntitiesResponse {
  entities: any[];
  page: number;
  per_page: number;
  total_entities: number;
  total_pages: number;
}

/**
 * `GET /api/admin/user/info/<uid>/listings` (ff-api `admin_user_info`
 * blueprint — note the singular `user/info`). The `{ status, data }` envelope's
 * `data` is `{ results, total, limit, offset }`; the whole set comes back in one
 * page, so the caller filters/pages client-side. (A bare array / `entities`
 * shape is tolerated too, in case the backend changes.)
 */
export const useUserEntitiesQuery = (uid: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: userContentKeys.entities(uid, {}),
    enabled: enabled && !!uid,
    queryFn: withErrorHandling(async () => {
      const raw = await request<any>(`/api/admin/user/info/${uid}/listings`);
      assertOkEnvelope(raw);
      const data = unwrap<any>(raw);
      const entities: any[] = Array.isArray(data)
        ? data
        : data?.results ?? data?.entities ?? [];
      return {
        entities,
        page: 1,
        per_page: entities.length,
        total_entities: data?.total ?? data?.total_entities ?? entities.length,
        total_pages: 1,
      } as UserEntitiesResponse;
    }, 'Failed to load the user’s listings'),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    retry: false,
  });
};

export const useOrgEntitiesQuery = (
  oid: number | null,
  params: { page?: number; per_page?: number } = {},
  enabled = true
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: userContentKeys.entities(oid ? -oid : null, { oid, ...params }),
    enabled: enabled && !!oid,
    queryFn: withErrorHandling(async () => {
      const raw = await request<any>(
        `/api/admin/entities${buildQs({ oid, ...params })}`
      );
      assertOkEnvelope(raw);
      const payload =
        raw?.data && raw.data.entities !== undefined ? raw.data : raw;
      return {
        entities: payload?.entities ?? [],
        page: payload?.page ?? 1,
        per_page: payload?.per_page ?? 0,
        total_entities: payload?.total_entities ?? 0,
        total_pages: payload?.total_pages ?? 0,
      } as UserEntitiesResponse;
    }, 'Failed to load the organization’s listings'),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    retry: false,
  });
};

/* ───────────────────────────── Notification feed ───────────────────────── */

export interface UserFeedItem {
  /** Feed-item id. Field name TBD by backend — read defensively in the UI. */
  id?: number | string;
  type?: string | null;
  title?: string | null;
  body?: string | null;
  message?: string | null;
  read?: boolean | number | null;
  entity_id?: number | null;
  eid?: number | null;
  actor_uid?: number | null;
  date_added?: string | null;
  created_at?: string | null;
  [key: string]: unknown;
}

export interface UserFeedResponse {
  feed: UserFeedItem[];
  count: number;
}

export const useUserFeedQuery = (
  uid: number | null,
  params: { page?: number; page_size?: number } = {},
  enabled = true
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: userContentKeys.feed(uid, params),
    enabled: enabled && !!uid,
    queryFn: withErrorHandling(
      () =>
        request<any>(
          `/api/admin/user/info/${uid}/notifications${buildQs(params)}`
        ).then((r) => {
          const body = unwrap<any>(r);
          const feed: UserFeedItem[] = Array.isArray(body)
            ? body
            : body?.notifications ?? body?.feed ?? body?.items ?? [];
          const count = Array.isArray(body)
            ? body.length
            : body?.count ?? feed.length;
          return { feed, count } as UserFeedResponse;
        }),
      'Failed to load the notification feed'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    retry: false,
  });
};

/* ─────────────────────────── User's organizations ──────────────────────── */

export interface UserOrganizationRow extends Partial<AdminOrganization> {
  oid: number;
  name: string;
  /** How this user relates to the org — owner vs. member. Backend-provided. */
  membership?: 'owner' | 'member' | string | null;
  role_description?: string | null;
}

export interface UserOrganizationsResponse {
  organizations: UserOrganizationRow[];
  count: number;
}

export const useUserOrganizationsQuery = (
  uid: number | null,
  enabled = true
) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: userContentKeys.organizations(uid),
    enabled: enabled && !!uid,
    queryFn: withErrorHandling(
      () =>
        request<any>(`/api/admin/user/info/${uid}/organizations`).then((r) => {
          const body = unwrap<any>(r);
          const rows: any[] = Array.isArray(body)
            ? body
            : body?.organizations ?? [];
          const organizations: UserOrganizationRow[] = rows.map((o) => ({
            ...o,
            membership:
              o.membership ?? (Number(o.uid) === uid ? 'owner' : 'member'),
          }));
          return {
            organizations,
            count: Array.isArray(body)
              ? organizations.length
              : body?.count ?? organizations.length,
          } as UserOrganizationsResponse;
        }),
      'Failed to load the user’s organizations'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    retry: false,
  });
};
